import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from './contexts/LanguageContext';
import { storage } from './utils/storage';
import { API_ENDPOINTS } from './config/api';
import './LoginPage.css';

const LoginPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate(); // 添加导航钩子
  const [isLogin, setIsLogin] = useState(true); // true为登录，false为注册
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState(''); // 添加用户名
  const [ieltsDeadline, setIeltsDeadline] = useState(''); // 添加雅思倒计时
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // 不清除用户数据，只检查登录状态
  useEffect(() => {
    // 不再修改用户数据，只检查是否已登录
    const userDataStr = localStorage.getItem('talktastic_user');
    if (userDataStr) {
      try {
        const userData = JSON.parse(userDataStr);
        // 如果用户已登录，则直接跳转到账户中心
        if (userData && userData.isRegistered && userData.isLoggedIn) {
          navigate('/account');
        }
      } catch (error) {
        console.error('解析用户数据失败:', error);
      }
    }
  }, [navigate]);

  // 表单提交处理
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        // 登录逻辑
        const response = await fetch(API_ENDPOINTS.login, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, password })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || '登录失败');
        }

        const data = await response.json();
        
        // 检查是否有现有用户数据（包含会员信息）
        const existingUserDataStr = localStorage.getItem('talktastic_user');
        let existingUserData = null;
        
        if (existingUserDataStr) {
          try {
            existingUserData = JSON.parse(existingUserDataStr);
          } catch (error) {
            console.error('解析现有用户数据失败:', error);
          }
        }
        
        // 创建新的用户数据对象，确保包含登录状态标记
        const mergedUserData = {
          ...data.user,
          isRegistered: true,
          isLoggedIn: true, // 设置为已登录状态
          loginTime: new Date().toISOString()
        };
        
        // 如果存在现有数据，保留会员状态相关字段
        if (existingUserData && existingUserData.email === data.user.email) {
          // 保留会员状态相关信息
          if (existingUserData.isPaid) {
            mergedUserData.isPaid = existingUserData.isPaid;
            mergedUserData.membershipExpiry = existingUserData.membershipExpiry;
          }
        }
        
        // 保存合并后的用户信息和token
        localStorage.setItem('talktastic_user', JSON.stringify(mergedUserData));
        localStorage.setItem('token', data.token);
        
        // 更新用户数据库
        const usersJson = localStorage.getItem('talktastic_users');
        let users = {};
        
        if (usersJson) {
          try {
            users = JSON.parse(usersJson);
          } catch (error) {
            console.error('解析用户数据库失败:', error);
          }
        }
        
        // 更新或添加用户信息
        users[data.user.email] = {
          ...data.user,
          isRegistered: true,
          isLoggedIn: true,
          loginTime: mergedUserData.loginTime
        };
        
        // 确保会员信息同步
        if (mergedUserData.isPaid) {
          users[data.user.email].isPaid = mergedUserData.isPaid;
          users[data.user.email].membershipExpiry = mergedUserData.membershipExpiry;
        }
        
        localStorage.setItem('talktastic_users', JSON.stringify(users));
        
        // 使用navigate跳转
        navigate('/account');
      } else {
        // 注册逻辑
        const response = await fetch(API_ENDPOINTS.register, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            password,
            username,
            ieltsDeadline: parseInt(ieltsDeadline) || 30
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || '注册失败');
        }

        const data = await response.json();

        // 添加登录状态标记
        const userData = {
          ...data.user,
          isRegistered: true,
          isLoggedIn: true,
          loginTime: new Date().toISOString()
        };
        
        // 保存用户信息和token
        localStorage.setItem('talktastic_user', JSON.stringify(userData));
        localStorage.setItem('token', data.token);
        
        // 更新用户数据库
        const usersJson = localStorage.getItem('talktastic_users');
        let users = {};
        
        if (usersJson) {
          try {
            users = JSON.parse(usersJson);
          } catch (error) {
            console.error('解析用户数据库失败:', error);
          }
        }
        
        // 添加新用户信息
        users[data.user.email] = userData;
        localStorage.setItem('talktastic_users', JSON.stringify(users));
        
        // 使用navigate跳转
        navigate('/account');
      }
    } catch (error) {
      console.error('Auth error:', error);
      setError(error.message || (language === 'en' ? 'An error occurred' : '发生错误'));
    } finally {
      setLoading(false);
    }
  };
  
  // 返回按钮处理
  const handleGoBack = () => {
    window.history.back();
  };

  // 切换登录/注册表单
  const toggleForm = () => {
    setIsLogin(!isLogin);
    setError('');
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <button onClick={handleGoBack} className="back-button">
            ← {language === 'en' ? 'Back' : '返回'}
          </button>
          <h1>{isLogin 
            ? (language === 'en' ? 'Login' : '登录') 
            : (language === 'en' ? 'Create Account' : '创建账号')}
          </h1>
          <p>
            {isLogin 
              ? (language === 'en' 
                  ? 'Login to access premium features' 
                  : '登录以使用付费功能') 
              : (language === 'en' 
                  ? 'Create an account to access premium features' 
                  : '创建账号以使用付费功能')}
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>{language === 'en' ? 'Email' : '邮箱'}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={language === 'en' ? 'Enter your email' : '输入您的邮箱'}
              required
            />
          </div>
          
          <div className="form-group">
            <label>{language === 'en' ? 'Password' : '密码'}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={language === 'en' ? 'Enter your password' : '输入您的密码'}
              required
            />
          </div>
          
          {!isLogin && (
            <>
              <div className="form-group">
                <label>{language === 'en' ? 'Confirm Password' : '确认密码'}</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={language === 'en' ? 'Confirm your password' : '再次输入密码'}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>{language === 'en' ? 'Username' : '用户名'}</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={language === 'en' ? 'Enter your username' : '输入您的用户名'}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>{language === 'en' ? 'IELTS Exam Deadline (days)' : '雅思考试倒计时 (天数)'}</label>
                <input
                  type="number"
                  value={ieltsDeadline}
                  onChange={(e) => setIeltsDeadline(e.target.value)}
                  placeholder={language === 'en' ? 'Enter days until your exam' : '输入距离考试的天数'}
                />
              </div>
            </>
          )}
          
          <button 
            type="submit" 
            className="submit-btn"
            disabled={loading}
          >
            {loading 
              ? (language === 'en' ? 'Processing...' : '处理中...') 
              : (isLogin 
                  ? (language === 'en' ? 'Login' : '登录') 
                  : (language === 'en' ? 'Register' : '注册'))}
          </button>
        </form>
        
        <div className="form-switch">
          {isLogin 
            ? (language === 'en' 
                ? "Don't have an account? " 
                : '还没有账号？') 
            : (language === 'en' 
                ? 'Already have an account? ' 
                : '已有账号？')}
          <button 
            onClick={toggleForm}
            className="switch-btn"
          >
            {isLogin 
              ? (language === 'en' ? 'Register now' : '立即注册') 
              : (language === 'en' ? 'Login' : '登录')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 
